if (!customElements.get('v5-stories')) {
  class V5Stories extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this.intersectionObserver = null;
      this.MIN_LOOP_SLIDES = 8;
    }

    connectedCallback() {
      this.swiperDOM = this.querySelector('.stories-swiper-v5');
      this.wrapper = this.swiperDOM?.querySelector('.swiper-wrapper') || null;
      this.nextEl = this.querySelector('.stories-next');
      this.prevEl = this.querySelector('.stories-prev');

      this.observeWhenVisible();
    }

    disconnectedCallback() {
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }

      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }

      this.removeClonedSlides();
    }

    observeWhenVisible() {
      if (!this.swiperDOM || !this.wrapper || this.swiper || this.intersectionObserver) return;

      this.intersectionObserver = new IntersectionObserver((entries) => {
        if (!entries[0]?.isIntersecting) return;

        this.initSwiper();
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }, {
        rootMargin: '200px 0px',
      });

      this.intersectionObserver.observe(this);
    }

    removeClonedSlides() {
      if (!this.wrapper) return;
      this.wrapper.querySelectorAll('[data-story-clone="true"]').forEach((slide) => slide.remove());
    }

    ensureMinimumSlides() {
      if (!this.wrapper) return 0;

      this.removeClonedSlides();

      const originalSlides = Array.from(this.wrapper.children).filter(
        (slide) => slide.dataset.storyClone !== 'true'
      );
      const originalCount = originalSlides.length;

      if (originalCount <= 1 || originalCount >= this.MIN_LOOP_SLIDES) {
        return originalCount;
      }

      const need = this.MIN_LOOP_SLIDES - originalCount;
      const fragment = document.createDocumentFragment();

      for (let i = 0; i < need; i++) {
        const clone = originalSlides[i % originalCount].cloneNode(true);
        clone.dataset.storyClone = 'true';
        fragment.appendChild(clone);
      }

      this.wrapper.appendChild(fragment);
      return originalCount;
    }

    initSwiper() {
      if (!this.swiperDOM || !this.wrapper || this.swiper) return;

      const originalCount = this.ensureMinimumSlides();
      if (originalCount <= 1) return;

      const prevBtnLabel = this.prevEl?.getAttribute('aria-label') || 'Previous slide';
      const nextBtnLabel = this.nextEl?.getAttribute('aria-label') || 'Next slide';
      const carouselLabel = this.swiperDOM?.getAttribute('aria-label') || 'Stories carousel';

      this.swiper = new Swiper(this.swiperDOM, {
        slidesPerView: 1.3,
        spaceBetween: 16,
        centeredSlides: true,
        loop: true,
        loopAdditionalSlides: 1,
        loopedSlides: Math.min(Math.max(originalCount, 1), 3),
        navigation: {
          nextEl: this.nextEl,
          prevEl: this.prevEl,
        },
        a11y: {
          prevSlideMessage: prevBtnLabel,
          nextSlideMessage: nextBtnLabel,
          paginationBulletMessage: 'Go to slide {{index}}',
          containerMessage: carouselLabel,
          containerRoleDescriptionMessage: 'carousel',
          slideRole: 'group',
        },
        breakpoints: {
          1024: {
            slidesPerView: 3.3,
            spaceBetween: 24,
          },
        },
      });
    }
  }

  customElements.define('v5-stories', V5Stories);
}
